import { useCallback, useEffect, useRef } from 'react';
import { driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { getCachedPreferences, setCachedPreference } from '../services/uxService';

/**
 * First-visit guided tour for a page (zero-pre-training UX, Tier 1).
 *
 * Steps target elements via `data-tour="<id>"` attributes so tours survive
 * CSS refactors. Completion is stored server-side per user (key
 * `tour:<tourId>`), so a cashier sees a tour once regardless of which till
 * machine they log in from. The returned `startTour` replays it on demand
 * (wire it to a help button).
 *
 * `enabled` should be false while the page is still loading, so the anchor
 * elements exist before the tour starts.
 */
export const usePageTour = (tourId: string, steps: DriveStep[], enabled: boolean = true) => {
  const startedRef = useRef(false);

  const run = useCallback(() => {
    const tour = driver({
      showProgress: true,
      overlayOpacity: 0.55,
      stagePadding: 6,
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Done',
      steps,
      onDestroyed: () => {
        setCachedPreference(`tour:${tourId}`, 'done');
      },
    });
    tour.drive();
  }, [tourId, steps]);

  useEffect(() => {
    if (!enabled || startedRef.current) return;

    let cancelled = false;
    getCachedPreferences().then((prefs) => {
      if (cancelled || startedRef.current) return;
      if (prefs[`tour:${tourId}`] === 'done') return;
      startedRef.current = true;
      // Give the page a beat to finish rendering its anchors.
      window.setTimeout(() => { if (!cancelled) run(); }, 600);
    });

    return () => { cancelled = true; };
  }, [enabled, tourId, run]);

  return { startTour: run };
};
